import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, ShieldCheck, Mail, KeyRound, AlertCircle, CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";

// Mode d'authentification client
const MODE = {
  PASSWORD: "password",  // email + password
  MAGIC:    "magic",     // email → recevoir un lien
  LEGACY:   "legacy",    // BC + code
};

const ClientLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [mode, setMode] = useState(MODE.PASSWORD);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orderNumber, setOrderNumber] = useState(params.get("bc") || "");
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [magicSent, setMagicSent] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true); setError(null);
    try {
      await api.clientLoginV2(email.trim().toLowerCase(), password);
      navigate("/client/welcome", { replace: true });
    } catch (err) {
      setError(err.message || "Identifiants invalides");
    } finally { setLoading(false); }
  };

  const submitMagic = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true); setError(null);
    try {
      await api.clientLoginV2(email.trim().toLowerCase(), null, true);
      setMagicSent(true);
    } catch (err) {
      // Endpoint retourne toujours 200 — l'erreur est rare (network)
      setError(err.message || "Impossible d'envoyer le lien");
    } finally { setLoading(false); }
  };

  const submitLegacy = async (e) => {
    e.preventDefault();
    if (!orderNumber || !accessCode) return;
    setLoading(true); setError(null);
    try {
      await api.clientLogin(orderNumber.trim().toUpperCase(), accessCode.trim().toUpperCase());
      navigate("/client/welcome", { replace: true });
    } catch (err) {
      setError(err.message || "Bon de commande ou code invalide");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="page-client-login" className="min-h-screen bg-[#F7F8FA] pb-16">
      <div aria-hidden className="absolute -top-32 -right-24 h-[420px] w-[420px] rounded-full bg-[#C8FF2F]/22 blur-3xl" />

      <header className="relative z-10 max-w-md mx-auto px-5 h-14 flex items-center">
        <Logo variant="full" size="xs" rounded="rounded-md" />
      </header>

      <main className="relative z-10 max-w-md mx-auto px-5 pt-8 wyvio-fade-in">
        <p className="text-[13px] font-medium text-[#6B7280]">Espace client</p>
        <h1 className="mt-2 text-[30px] leading-[1.05] font-semibold tracking-tight text-[#111111]">
          Retrouvez votre suivi wyvio.
        </h1>
        <p className="mt-3 text-[14px] text-[#374151]">
          Utilisez votre email et votre mot de passe, ou demandez un nouveau lien magique.
        </p>

        {/* Toggle mode */}
        <div className="mt-6 grid grid-cols-2 gap-1.5 p-1.5 bg-white rounded-full border border-[#EEF2F5]">
          <button
            onClick={() => { setMode(MODE.PASSWORD); setError(null); setMagicSent(false); }}
            data-testid="mode-password"
            className={[
              "h-10 rounded-full text-[13px] font-semibold flex items-center justify-center gap-2 transition-all",
              mode === MODE.PASSWORD ? "bg-[#111111] text-white" : "text-[#6B7280] hover:text-[#111111]",
            ].join(" ")}
          >
            <KeyRound size={13} strokeWidth={2.25} /> Mot de passe
          </button>
          <button
            onClick={() => { setMode(MODE.MAGIC); setError(null); setMagicSent(false); }}
            data-testid="mode-magic"
            className={[
              "h-10 rounded-full text-[13px] font-semibold flex items-center justify-center gap-2 transition-all",
              mode === MODE.MAGIC ? "bg-[#111111] text-white" : "text-[#6B7280] hover:text-[#111111]",
            ].join(" ")}
          >
            <Mail size={13} strokeWidth={2.25} /> Lien magique
          </button>
        </div>

        {/* Formulaire */}
        {mode === MODE.PASSWORD && (
          <form onSubmit={submitPassword} className="mt-6 space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre email"
              autoFocus
              required
              data-testid="login-email"
              className="w-full h-12 px-4 rounded-2xl border border-[#E5E7EB] bg-white text-[14px] focus:outline-none focus:border-[#111111] transition-colors"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe"
              required
              data-testid="login-password"
              className="w-full h-12 px-4 rounded-2xl border border-[#E5E7EB] bg-white text-[14px] focus:outline-none focus:border-[#111111] transition-colors"
            />
            {error && (
              <div data-testid="login-error" className="flex items-start gap-2 rounded-2xl bg-[#FEF2F2] text-[#991B1B] px-4 py-3 text-[13px]">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-12 rounded-full bg-[#111111] text-white text-[14px] font-semibold flex items-center justify-center gap-2 hover:bg-black active:scale-[0.99] transition-all shadow-[0_14px_40px_-14px_rgba(17,17,17,0.5)] disabled:opacity-50"
            >
              {loading ? "…" : (<><span>Se connecter</span><ArrowRight size={16} strokeWidth={2.25} /></>)}
            </button>
          </form>
        )}

        {mode === MODE.MAGIC && (
          <form onSubmit={submitMagic} className="mt-6 space-y-3">
            {!magicSent ? (
              <>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Votre email"
                  autoFocus
                  required
                  data-testid="magic-email"
                  className="w-full h-12 px-4 rounded-2xl border border-[#E5E7EB] bg-white text-[14px] focus:outline-none focus:border-[#111111] transition-colors"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="magic-submit"
                  className="w-full h-12 rounded-full bg-[#C8FF2F] text-[#111111] text-[14px] font-semibold flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.99] transition-all shadow-[0_10px_30px_-14px_rgba(200,255,47,0.7)] disabled:opacity-50"
                >
                  {loading ? "…" : (<><span>Recevoir un lien</span><ArrowRight size={16} strokeWidth={2.25} /></>)}
                </button>
                <p className="text-[11.5px] text-[#9CA3AF] text-center">
                  Un lien sécurisé valable 24 heures vous sera envoyé.
                </p>
              </>
            ) : (
              <div
                data-testid="magic-sent"
                className="rounded-2xl border border-[#C8FF2F] bg-[#F7FFC9]/40 px-4 py-4 flex items-start gap-3"
              >
                <div className="h-9 w-9 rounded-full bg-[#C8FF2F] text-[#111111] flex items-center justify-center shrink-0">
                  <CheckCircle2 size={16} strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-[#111111]">
                    Lien envoyé.
                  </p>
                  <p className="text-[12px] text-[#374151] mt-1">
                    Si un dossier est associé à cette adresse, vous recevrez un email dans quelques instants.
                  </p>
                </div>
              </div>
            )}
          </form>
        )}

        {/* Legacy BC+code — toujours accessible */}
        <div className="mt-8 text-center">
          <button
            onClick={() => { setMode(MODE.LEGACY); setError(null); setMagicSent(false); }}
            data-testid="mode-legacy"
            className="text-[12.5px] text-[#6B7280] hover:text-[#111111] font-medium underline underline-offset-4 transition-colors"
          >
            Se connecter avec un bon de commande et un code
          </button>
        </div>

        {mode === MODE.LEGACY && (
          <form onSubmit={submitLegacy} className="mt-4 space-y-3">
            <input
              type="text"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
              placeholder="BC-2026-0000"
              required
              data-testid="legacy-order"
              className="w-full h-12 px-4 rounded-2xl border border-[#E5E7EB] bg-white text-[14px] tracking-wider font-mono focus:outline-none focus:border-[#111111] transition-colors"
            />
            <input
              type="text"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
              placeholder="Code d'accès"
              required
              data-testid="legacy-code"
              className="w-full h-12 px-4 rounded-2xl border border-[#E5E7EB] bg-white text-[14px] tracking-widest font-mono focus:outline-none focus:border-[#111111] transition-colors"
            />
            {error && (
              <div className="flex items-start gap-2 rounded-2xl bg-[#FEF2F2] text-[#991B1B] px-4 py-3 text-[13px]">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="legacy-submit"
              className="w-full h-12 rounded-full bg-[#111111] text-white text-[14px] font-semibold flex items-center justify-center gap-2 hover:bg-black active:scale-[0.99] transition-all disabled:opacity-50"
            >
              {loading ? "…" : "Accéder à mon suivi"}
            </button>
          </form>
        )}

        <div className="mt-10 text-center text-[11.5px] text-[#9CA3AF] flex items-center justify-center gap-1.5">
          <ShieldCheck size={12} /> Connexion chiffrée · Vos données sont protégées
        </div>
      </main>
    </div>
  );
};

export default ClientLogin;
